import { StrictMode, Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Route, Routes } from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "./index.css";
import { CheckEmail, ForgotPassword, FullPageSpinner, Home, Invite, Login, MagicLink, ResetPassword, Signup, VerifyEmail, Welcome } from "./pages/auth";
import { Account, Members, OrgHome, OrgSettings, OrgShell } from "./pages/org";
import { ChatHome, ChatLayout, ConversationView } from "./pages/chat";
import { CallsPage } from "./pages/calls";
import { MeetingsPage } from "./pages/meetings";
import { AdminPage } from "./pages/admin";
import { ReportsPage } from "./pages/reports";
import { BillingPage } from "./pages/billing";
import { RecordingsPage } from "./pages/recordings";
import { TranscriptPage, TranscriptsPage } from "./pages/transcripts";
import { ProjectPage, ProjectsPage } from "./pages/projects";
import { PlatformAdmins, PlatformAnnouncements, PlatformHealth, PlatformPlans, PlatformAudit, PlatformBilling, PlatformLayout, PlatformOrganization, PlatformAccess, PlatformOrganizations, PlatformOverview, PlatformUser, PlatformUsers } from "./pages/platform";
import { PlatformOrgUsage, PlatformUsage } from "./pages/platform/usage";
import { APP_NAME } from "./lib/api";

document.title = APP_NAME;
// The call screen brings the media library: loaded only when someone joins a call.
const CallPage = lazy(() => import("./pages/calls/call").then((m) => ({ default: m.CallPage })));
const MeetingJoinPage = lazy(() => import("./pages/meetings/join").then((m) => ({ default: m.MeetingJoinPage })));
const queryClient = new QueryClient({ defaultOptions: { queries: { refetchOnWindowFocus: false } } });

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/check-email" element={<CheckEmail />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/magic-link" element={<MagicLink />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/invite" element={<Invite />} />
          <Route path="/welcome" element={<Welcome />} />
          <Route path="/admin" element={<PlatformLayout />}>
            <Route index element={<PlatformOverview />} />
            <Route path="organizations" element={<PlatformOrganizations />} />
            <Route path="organizations/:id" element={<PlatformOrganization />} />
            <Route path="access" element={<PlatformAccess />} />
            <Route path="usage" element={<PlatformUsage />} />
            <Route path="usage/:orgId" element={<PlatformOrgUsage />} />
            <Route path="users" element={<PlatformUsers />} />
            <Route path="users/:id" element={<PlatformUser />} />
            <Route path="billing" element={<PlatformBilling />} />
            <Route path="admins" element={<PlatformAdmins />} />
            <Route path="announcements" element={<PlatformAnnouncements />} />
            <Route path="health" element={<PlatformHealth />} />
            <Route path="plans" element={<PlatformPlans />} />
            <Route path="audit" element={<PlatformAudit />} />
          </Route>
          <Route path="/m/:code" element={<Suspense fallback={<FullPageSpinner />}><MeetingJoinPage /></Suspense>} />
          <Route path="/o/:slug" element={<OrgShell />}>
            <Route index element={<OrgHome />} />
            <Route path="chat" element={<ChatLayout />}>
              <Route index element={<ChatHome />} />
              <Route path=":conversationId" element={<ConversationView />} />
            </Route>
            <Route path="calls" element={<CallsPage />} />
            <Route path="call/:callId" element={<Suspense fallback={<FullPageSpinner />}><CallPage /></Suspense>} />
            <Route path="meetings" element={<MeetingsPage />} />
            <Route path="projects" element={<ProjectsPage />} />
            <Route path="projects/:projectId" element={<ProjectPage />} />
            <Route path="members" element={<Members />} />
            <Route path="settings" element={<OrgSettings />} />
            <Route path="admin" element={<AdminPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="billing" element={<BillingPage />} />
            <Route path="recordings" element={<RecordingsPage />} />
            <Route path="transcripts" element={<TranscriptsPage />} />
            <Route path="transcripts/:transcriptId" element={<TranscriptPage />} />
            <Route path="account" element={<Account />} />
          </Route>
          <Route path="*" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
